import React, { useEffect, useState } from 'react'
import { Dialog } from '../../components/ui/dialog'
import { Spinner, Icon } from '@blueprintjs/core'
import { Card } from '../../components/ui/card'
import { axiosDestroy, axiosPost } from '../../util/api'
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import ReactCreditCards from 'react-credit-cards'
import { useDispatch, useSelector } from 'react-redux'
import { fetchCards } from '../../actions/cards'
import { Layout } from '../../components/Layout'
import { Toasts } from '../../components/toaster/Toast'
import { CardHeader } from '../../components/ui/CardHeader'
import { Link } from 'react-router-dom'
import { stripePromise } from '../../util/stripe_promise'
import { Button } from '../../components/ui/button'
import { EmptyState } from '../../components/ui/empty-state'

const breadcrumbs = (
    <b className="text-lg">
        <Link to="/billing">Billing</Link>&nbsp;› Cards
    </b>
)

export const Cards = () => {
    const dispatch = useDispatch()
    const { cards, loading, defaultCard } = useSelector((store) => store.cards)
    const [defaulting, setDefaultingRaw] = useState({})
    const setDefaulting = (id, defaulting) =>
        setDefaultingRaw((prev) => ({
            ...prev,
            [id]: defaulting,
        }))
    const [deleting, setDeletingRaw] = useState({})
    const setDeleting = (id, deleting) =>
        setDeletingRaw((prev) => ({
            ...prev,
            [id]: deleting,
        }))

    useEffect(() => {
        dispatch(fetchCards())
    }, [])

    const [addingCard, setAddingCard] = useState(false)

    const setDefault = (id) => {
        setDefaulting(id, true)
        axiosPost(`billing/cards/default`, { id })
            .then(() => {
                Toasts.success('Default card updated')
                setDefaulting(id, false)
                dispatch(fetchCards())
            })
            .catch((err) => {
                console.log('err', err)
                setDefaulting(id, false)
            })
    }

    const deleteCard = (id) => {
        if (confirm('Are you sure you want to delete this card?')) {
            setDeleting(id, true)
            axiosDestroy(`billing/cards`, { id })
                .then((res) => {
                    Toasts.success('Card deleted')
                    setDeleting(id, false)
                    dispatch(fetchCards())
                })
                .catch((err) => {
                    console.log('err', err)
                    setDeleting(id, false)
                })
        }
    }

    console.log('cards', cards)

    return (
        <Layout primary={breadcrumbs}>
            <div className="max-w-3xl mx-auto" align="left">
                <Card className="p-0 bp4-card-rounded minimal">
                    <CardHeader title=" " minimal>
                        <Button
                            intent="primary"
                            type="submit"
                            text="Add Card"
                            onClick={() => setAddingCard(true)}
                        />
                    </CardHeader>
                    {loading && <Spinner className="mt-20" size={60} />}
                    {!loading && (
                        <div className="flex flex-row justify-around">
                            <div>
                                <Elements stripe={stripePromise}>
                                    <Dialog
                                        isOpen={addingCard}
                                        className="p-0"
                                        onClose={() => setAddingCard(false)}
                                    >
                                        <AddCard
                                            onSave={() => {
                                                setAddingCard(false)
                                                dispatch(fetchCards())
                                            }}
                                        />
                                    </Dialog>
                                </Elements>
                            </div>
                        </div>
                    )}
                </Card>

                {!loading && cards.length > 0 && (
                    <div className="flex flex-wrap">
                        {cards.map((card) => (
                            <div key={card.id} className="p-4 mr-6 mb-6 relative">
                                <div className="relative">
                                    <div
                                        className="absolute opacity-0 hover:opacity-100 transition-opacity duration-100 w-full h-full"
                                        style={{ zIndex: 98 }}
                                    >
                                        <div
                                            style={{ zIndex: 99, borderRadius: '14.5px' }}
                                            className="absolute opacity-50 bg-gray-700 w-full h-full"
                                        />
                                        <div className="opacity-100 absolute flex flex-col items-center justify-center w-full h-full">
                                            {card.id !== defaultCard && (
                                                <Button
                                                    style={{
                                                        opacity: '100% !important',
                                                        zIndex: 1000,
                                                    }}
                                                    className="mb-3"
                                                    disabled={deleting[card.id]}
                                                    loading={defaulting[card.id]}
                                                    onClick={() => setDefault(card.id)}
                                                    text="Set as Default"
                                                />
                                            )}

                                            <Button
                                                style={{ opacity: '100% !important', zIndex: 1000 }}
                                                intent="danger"
                                                disabled={defaulting[card.id]}
                                                loading={deleting[card.id]}
                                                onClick={() => deleteCard(card.id)}
                                                text="Delete"
                                            />
                                        </div>
                                    </div>
                                    {card.id === defaultCard && (
                                        <div
                                            className="absolute w-full h-full mt-3"
                                            style={{ zIndex: 97 }}
                                        >
                                            <div className="w-full h-full flex justify-center items-end">
                                                <div className="border border-blue-500 rounded shadow-lg inline bg-blue-100 bg-opacity-100 text-blue-500 px-3 py-2">
                                                    Default
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <ReactCreditCards
                                        cvc={''}
                                        placeholders={{
                                            name: '',
                                        }}
                                        expiry={`${('00' + card.card.exp_month).substr(-2, 2)}/${card.card.exp_year}`}
                                        focused="false"
                                        name={card.billing_details.name || ''}
                                        preview
                                        issuer={card.card.brand}
                                        number={'************' + card.card.last4}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {!loading && cards.length === 0 && (
                    <EmptyState
                        title="No cards to show"
                        icon={<Icon icon="search" size={60} />}
                        description={
                            <div>
                                <p>
                                    To add add a new card to your account by clicking the Add Card
                                    button
                                </p>
                                <Button
                                    text="Add Card"
                                    intent="primary"
                                    large
                                    onClick={() => setAddingCard(true)}
                                />
                            </div>
                        }
                    />
                )}
            </div>
        </Layout>
    )
}

export const AddCard = ({ onSave }) => {
    const stripe = useStripe()
    const elements = useElements()
    const [loading, setLoading] = useState(false)

    const handleSubmit = async (event) => {
        // Block native form submission.
        event.preventDefault()

        if (!stripe || !elements) {
            // Stripe.js has not loaded yet. Make sure to disable
            // form submission until Stripe.js has loaded.
            return
        }

        // Get a reference to a mounted CardElement. Elements knows how
        // to find your CardElement because there can only ever be one of
        // each type of element.
        const cardElement = elements.getElement(CardElement)

        // Use your card Element with other Stripe.js APIs
        setLoading(true)
        const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
        })

        if (error) {
            setLoading(false)
            Toasts.error(error.message)
        } else {
            axiosPost('billing/cards', paymentMethod)
                .then((res) => {
                    setLoading(false)
                    onSave()
                })
                .catch((err) => {
                    setLoading(false)
                })
        }
    }

    return (
        <div className="py-4">
            <CardElement
                className="border rounded-md p-3"
                options={{
                    style: {
                        base: {
                            fontSize: '16px',
                            color: '#424770',
                            '::placeholder': {
                                color: '#aab7c4',
                            },
                        },
                        invalid: {
                            color: '#9e2146',
                        },
                    },
                }}
            />
            <img
                className="my-3"
                alt="Powered by Stripe"
                src="/powered_by_stripe.png"
                style={{ maxWidth: '100px', opacity: 0.6 }}
            />
            <div className="flex justify-between">
                <Button
                    fill
                    loading={loading}
                    className="mt-3"
                    large
                    disabled={!stripe || !elements}
                    intent="primary"
                    text="Add Card"
                    onClick={handleSubmit}
                />
            </div>
        </div>
    )
}
